import { Formik } from "formik";
import { UtilityFormRenderer } from "./UtilityFormRenderer";
import React from "react";
import { Box, Typography } from "@mui/material";
import { StepsButton } from "./StepsButtons";
import { useDispatch, useSelector } from "react-redux";
import { setActiveStep } from "../redux/stepperROWSlice";
import { AppDispatch, RootReducerType } from "@store/store";
import { Section, YesNoQuestion } from "../types";
import * as Yup from "yup";
import { setSiteRoadwaysROWField } from "../redux/siteRoadwaysROWSlice";
import { toast } from "react-toastify";
import { submitSiteRoadwaysROWThunk } from "../redux/thunk/sideRoadwaysThunk";
import { sectionsIfOFC, sectionsNoOFC } from "./rowStaticData";

export const SiteRoadwaysROW: React.FC = React.memo(() => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeStep } = useSelector(
    (state: RootReducerType) => state.stepperROW,
  );

  const { access_id, amenity, utility_type, application_id } = useSelector(
    (state: RootReducerType) => state.amenitiesROW,
  );

  const siteRoadwaysROW = useSelector(
    (state: RootReducerType) => state.siteRoadwaysROW,
  );
  const applicationEditId = useSelector(
    (state: RootReducerType) => state.applicationDashboard?.id,
  );

  const sections = amenity === "OFC" ? sectionsIfOFC : sectionsNoOFC;
  const applicationID = applicationEditId || access_id;

  // Get all yes/no questions from sections
  const getYesNoQuestions = (sections: Section[]): YesNoQuestion[] => {
    const questions: YesNoQuestion[] = [];

    const traverseFields = (fields: any[]) => {
      for (const field of fields) {
        if (field.type === "radio" && field.id !== undefined) {
          questions.push({
            id: field.id,
            name: field.name,
            label: field.title,
          });
        }
        if (field.nestedFields && Array.isArray(field.nestedFields)) {
          traverseFields(field.nestedFields);
        }
      }
    };

    for (const section of sections) {
      traverseFields(section.fields);
    }

    return questions;
  };

  const yesNoQuestions = getYesNoQuestions(sections);

  // Function to get all visible fields based on current form values
  const getVisibleFields = (values: Record<string, any>): string[] => {
    const visibleFields: string[] = [];

    const traverseFields = (fields: any[], parentValue?: string) => {
      for (const field of fields) {
        // Always include the field if it has no parent or parent is "Yes"
        const isVisible = !parentValue || parentValue === "Yes";

        if (isVisible) {
          // Add field name if it has a name property
          if (field.name && field.type !== "radio") {
            visibleFields.push(field.name);
          }

          // If this is a radio field, add it to visible fields
          if (field.type === "radio" && field.name) {
            visibleFields.push(field.name);
          }

          // Recursively traverse nested fields
          if (field.nestedFields && Array.isArray(field.nestedFields)) {
            const currentFieldValue = field.name
              ? values[field.name]
              : undefined;
            traverseFields(field.nestedFields, currentFieldValue);
          }
        }
      }
    };

    for (const section of sections) {
      traverseFields(section.fields);
    }

    // Remove duplicates using array methods (avoiding Set iteration issues)
    const uniqueFields: string[] = [];
    visibleFields.forEach((field) => {
      if (!uniqueFields.includes(field)) {
        uniqueFields.push(field);
      }
    });

    return uniqueFields;
  };

  const {
    existing_duct_dimension_details,
    duct_utility_details,
    ofc_damage_details,
    right_of_use,
    utilized_nh_land_area,
    proposed_utility_dimension,
    circle_rate,
    available_row_width_left,
    available_row_width_right,
    proposed_utility_distance_left,
    proposed_utility_distance_right,
    land_acquisition_proposal_left,
    land_acquisition_proposal_right,
    total_land_to_be_acquired,
    already_laid_services_desc,
    number_of_existing_lanes,
    left_side_center_line,
    right_side_center_line,
    proposed_service_road_left,
    proposed_service_road_right,
    cable_position_within_right_of_way,
    utility_crossing_arrangement_id,
    cable_encasement_type,
    utility_line_casing_type,
    casing_depth_meters,
    waterway_prevention_compliance,
    laying_methodology,
    trench_refilling_methodology,
    trench_width_over_pipe_cm,
    trench_bedding_depth_cm,
    side_fill_layer_thickness_cm,
    distance_from_duct_edge_within_corridor,
    distance_from_edge_within_corridor,
    questions_true,
    questions_false,
  } = siteRoadwaysROW || {};

  const initialValues: Record<string, any> = {
    ...yesNoQuestions.reduce(
      (acc, question) => {
        if (questions_true?.includes(question.id)) {
          acc[question.name] = "Yes";
        } else if (questions_false?.includes(question.id)) {
          acc[question.name] = "No";
        } else {
          acc[question.name] = "";
        }
        return acc;
      },
      {} as Record<string, any>,
    ),

    existing_duct_dimension_details,
    duct_utility_details,
    ofc_damage_details,
    right_of_use,
    utilized_nh_land_area,
    proposed_utility_dimension,
    circle_rate,
    available_row_width_left,
    available_row_width_right,
    proposed_utility_distance_left,
    proposed_utility_distance_right,
    land_acquisition_proposal_left,
    land_acquisition_proposal_right,
    total_land_to_be_acquired,
    already_laid_services_desc,
    number_of_existing_lanes,
    left_side_center_line,
    right_side_center_line,
    proposed_service_road_left,
    proposed_service_road_right,
    cable_position_within_right_of_way,
    utility_crossing_arrangement_id,
    cable_encasement_type,
    utility_line_casing_type,
    casing_depth_meters,
    waterway_prevention_compliance,
    laying_methodology,
    trench_refilling_methodology,
    trench_width_over_pipe_cm,
    trench_bedding_depth_cm,
    side_fill_layer_thickness_cm,
    distance_from_duct_edge_within_corridor,
    distance_from_edge_within_corridor,
    questions_true,
    questions_false,
  };

  const backButtonClickHandler = () => {
    dispatch(setActiveStep(activeStep - 1));
  };

  // Function to get all field definitions (memoized to avoid recalculation)
  const getAllFieldDefinitions = React.useMemo(() => {
    const allFields: any[] = [];

    const traverseFields = (fields: any[]) => {
      for (const field of fields) {
        allFields.push(field);
        if (field.nestedFields && Array.isArray(field.nestedFields)) {
          traverseFields(field.nestedFields);
        }
      }
    };

    for (const section of sections) {
      traverseFields(section.fields);
    }

    return allFields;
  }, [sections]);

  // Custom validation function
  const validateForm = React.useCallback(
    (values: Record<string, any>) => {
      const errors: Record<string, string> = {};
      const visibleFields = getVisibleFields(values);

      // Create a map of field definitions by name for quick lookup
      const fieldMap = new Map();
      getAllFieldDefinitions.forEach((field) => {
        if (field.name) {
          fieldMap.set(field.name, field);
        }
      });

      visibleFields.forEach((fieldName) => {
        const value = values[fieldName];
        const fieldDef = fieldMap.get(fieldName);

        if (fieldDef && fieldDef.required !== false) {
          // Validate required fields
          if (fieldDef.type === "radio" && !value) {
            errors[fieldName] = "Required";
          } else if (
            (fieldDef.type === "text" || fieldDef.type === "select") &&
            !value
          ) {
            errors[fieldName] = "Required";
          }
        }

        // Number validation
        if (
          fieldDef &&
          fieldDef.dataType === "number" &&
          value &&
          isNaN(Number(value))
        ) {
          errors[fieldName] = "Must be a number";
        }
      });

      return errors;
    },
    [getAllFieldDefinitions],
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={validateForm}
      onSubmit={async (values: any) => {
        const questions_true: number[] = [];
        const questions_false: number[] = [];

        yesNoQuestions.forEach(({ id, name }) => {
          const answer = values[name];
          if (answer === "Yes") {
            questions_true.push(id);
          } else if (answer === "No") {
            questions_false.push(id);
          }
        });

        const extractTextAndSelectFields = (
          sections: Section[],
          values: any,
        ): Record<string, any> => {
          const payload: Record<string, any> = {};

          const traverseFields = (fields: any[]) => {
            for (const field of fields) {
              // Skip radio fields with IDs (handled by questions_true/false)
              if (field.type === "radio" && field.id !== undefined) {
                if (field.nestedFields && Array.isArray(field.nestedFields)) {
                  traverseFields(field.nestedFields);
                }
                continue;
              }

              // Process radio fields without IDs
              if (
                field.type === "radio" &&
                field.name &&
                field.id === undefined
              ) {
                const value = values[field.name];
                if (value && value !== "0" && value !== 0) {
                  payload[field.name] = value;
                }
              }

              // Process text and select fields
              if (field.type === "text" || field.type === "select") {
                const rawValue = values[field.name];
                const processedValue =
                  field.dataType === "number" ? Number(rawValue) : rawValue;
                const isBlank =
                  processedValue === "" ||
                  processedValue === null ||
                  processedValue === undefined ||
                  (typeof processedValue === "number" &&
                    (processedValue === 0 || isNaN(processedValue))) ||
                  processedValue === "0";
                if (!isBlank) {
                  payload[field.name] = processedValue;
                }
              }

              // Recursively process nested fields
              if (field.nestedFields && Array.isArray(field.nestedFields)) {
                traverseFields(field.nestedFields);
              }
            }
          };

          for (const section of sections) {
            traverseFields(section.fields);
          }

          return payload;
        };

        const inputPayload = extractTextAndSelectFields(sections, values);
        const stringifiedInputPayload = Object.fromEntries(
          Object.entries(inputPayload)
            .filter(([, value]) => {
              const str = String(value);
              return (
                value !== null &&
                value !== undefined &&
                str !== "" &&
                str !== "0"
              );
            })
            .map(([key, value]) => [key, String(value)]),
        );
        const finalPayload = {
          application_id: applicationID,
          utility_type: utility_type,
          questions_true,
          questions_false,
          ...stringifiedInputPayload,
        };
        const response = await dispatch(
          submitSiteRoadwaysROWThunk(finalPayload),
        );

        if (response.payload.success) {
          toast.success("ROW Utility Information saved successfully");
          dispatch(setActiveStep(activeStep + 1));
        } else {
          toast.error(response.payload.message || "Failed to save application");
        }
      }}
    >
      {({
        handleSubmit,
        isValid,
        dirty,
        errors,
        values,
        setFieldValue,
        handleChange,
      }) => {
        // Calculate if form is valid based on visible fields only
        const visibleFields = getVisibleFields(values);
        const fieldMap = new Map();

        getAllFieldDefinitions.forEach((field) => {
          if (field.name) {
            fieldMap.set(field.name, field);
          }
        });

        let isFormValid = true;

        // Check if all visible required fields have values
        for (const fieldName of visibleFields) {
          const fieldDef = fieldMap.get(fieldName);
          const value = values[fieldName];

          if (fieldDef && fieldDef.required !== false) {
            if (fieldDef.type === "radio" && !value) {
              isFormValid = false;
              break;
            } else if (
              (fieldDef.type === "text" || fieldDef.type === "select") &&
              !value
            ) {
              isFormValid = false;
              break;
            }
          }

          // Number validation
          if (
            fieldDef &&
            fieldDef.dataType === "number" &&
            value &&
            isNaN(Number(value))
          ) {
            isFormValid = false;
            break;
          }
        }

        return (
          <Box className="amenities-container" component="div">
            <Box className="form-wrapper" sx={{ paddingTop: 0 }}>
              <form onSubmit={handleSubmit}>
                <UtilityFormRenderer
                  sections={sections}
                  handleFieldChange={(name, value) => {
                    // Update Formik value
                    setFieldValue(name, value);

                    // Update Redux
                    dispatch(setSiteRoadwaysROWField({ name, value }));

                    // Build questions_true / questions_false dynamically
                    const updatedValues = { ...values, [name]: value };
                    const updatedQuestionsTrue: number[] = [];
                    const updatedQuestionsFalse: number[] = [];

                    yesNoQuestions.forEach(({ id, name: qName }) => {
                      const answer =
                        qName === name ? value : updatedValues[qName];
                      if (answer === "Yes") {
                        updatedQuestionsTrue.push(id);
                      } else if (answer === "No") {
                        updatedQuestionsFalse.push(id);
                      }
                    });

                    // Update Redux with new arrays
                    dispatch(
                      setSiteRoadwaysROWField({
                        name: "questions_true",
                        value: updatedQuestionsTrue,
                      }),
                    );
                    dispatch(
                      setSiteRoadwaysROWField({
                        name: "questions_false",
                        value: updatedQuestionsFalse,
                      }),
                    );
                  }}
                />
              </form>
            </Box>
            <Box className="stepper-btns gray:br-t-500 mt-3">
              <StepsButton
                onBack={() => backButtonClickHandler()}
                onNext={() => handleSubmit()}
                nextDisabled={!isFormValid}
              />
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
});
